import { Grid, GridItem, HStack, VStack, Text, FormControl } from '@cad-arc';
import { uniqBy } from 'lodash';
import {
    UserOption,
    AttributeOption,
    EditUnitFormType,
    EditUnitOptions,
    EditUnitStatics,
} from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { SortableList } from '../../common/components/sortable-list';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { Controller, useFieldArray, useWatch, UseFormReturn } from 'react-hook-form';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';
import { FormTimeInput } from '../../forms/integrated-form-controls/form-time-input';
import { isValidSelectedPersonnel } from '../helpers';
import { AddPersonnelButton } from './add-personnel-button';
import { CallSignSelect } from './call-sign-select';
import { StationAndBeatFields } from './station-and-beat-fields';
import { UserSelectWithAgencyAndSkills } from './user-select-with-agency-and-skills';
import { useSetStaffCountBasedOnPersonnel } from './use-set-staff-count-based-on-personnel';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { VehicleSelect } from './vehicle-select';
import { StaffCountInput } from './staff-count-input';

export const LogOnEditUnitForm = React.memo(
    function LogOnEditUnitForm({
        form,
        formFieldOptions,
        statics,
        isEditUnit,
        setUserChangedStaffCount,
    }: {
        form: UseFormReturn<EditUnitFormType>;
        formFieldOptions: EditUnitOptions;
        statics: EditUnitStatics;
        isEditUnit: boolean;
        setUserChangedStaffCount?: Dispatch<SetStateAction<boolean>>;
    }): React.ReactElement {
        const { control } = form;
        const {
            staffCountEnabled,
            rollOutVehicleRequiredUnitSettingEnabled,
            rollOutUnitMemberOrderingEnabled,
        } = useEnabledFeatures();
        const { isFirstResponder } = useApplicationType();

        const alreadyInUnitUserIdsMap: Record<number, true> = React.useMemo(() => {
            const map: Record<number, true> = {};
            for (const id of statics.alreadyInUnitUserIds) {
                map[id] = true;
            }
            return map;
        }, [statics.alreadyInUnitUserIds]);
        const { fields, append, remove, replace } = useFieldArray({
            control,
            name: 'personnel',
        });
        const callSignValue = useWatch({
            control,
            name: 'callSign',
        });
        const secondaryUnitTypes = useWatch({
            control,
            name: 'secondaryUnitTypes',
        });
        // Hide the personnel error until the user picks a member or submits,
        // so a newly appended empty row isn't flagged right away.
        const [hasChangedPersonnel, setHasChangedPersonnel] = React.useState(false);

        function renderPersonnelRow(index: number): React.ReactElement {
            return (
                <Controller
                    key={fields[index].id}
                    name={`personnel.${index}`}
                    control={control}
                    render={({ field }) => (
                        <UserSelectWithAgencyAndSkills
                            {...field}
                            onChange={(e) => {
                                field.onChange(e);
                                setHasChangedPersonnel(true);
                                // RHF doesn't revalidate the array root rule when a single row changes.
                                form.trigger('personnel');
                            }}
                            data-testid="personnel"
                            aria-label={`Personnel ${index}`}
                            isDisabled={!callSignValue}
                            filterOption={(option) => {
                                return !alreadyInUnitUserIdsMap[option.value as number];
                            }}
                            index={index}
                            remove={remove}
                        />
                    )}
                />
            );
        }

        useSetStaffCountBasedOnPersonnel(form, isEditUnit);

        useEffect(() => {
            const subscription = form.watch((_data, { name, type }) => {
                if (name === 'callSign' && type === 'change') {
                    form.resetField('station');
                    form.resetField('vehicleTag');
                    if (!isEditUnit) {
                        form.resetField('personnel');
                        setHasChangedPersonnel(false);
                    }
                }
            });
            return () => {
                subscription.unsubscribe();
            };
        }, [form, isEditUnit]);

            return (
                <Grid templateColumns="repeat(5, 1fr)" gap={4}>
                    <GridItem colSpan={isFirstResponder ? 5 : 4}>
                        <CallSignSelect control={control} formFieldOptions={formFieldOptions} />
                    </GridItem>
                    <GridItem colSpan={isFirstResponder ? 5 : 4}>
                        <VStack alignItems="start">
                            <FormSelect
                                name="primaryUnitType"
                                control={control}
                                label="Primary Unit Type"
                                isDisabled={!callSignValue}
                                options={formFieldOptions.unitTypeOptions}
                                rules={{
                                    required: 'Required',
                                }}
                            />
                            {secondaryUnitTypes?.length > 0 ? (
                                <Text variant="caption">
                                    Secondary Unit Types:{' '}
                                    {secondaryUnitTypes
                                        .map((option: AttributeOption) => option.label)
                                        .join(', ')}
                                </Text>
                            ) : undefined}
                        </VStack>
                    </GridItem>
                    <GridItem colSpan={3}>
                        <FormSelect
                            name="dispatchArea"
                            control={control}
                            rules={{
                                required: 'Required',
                            }}
                            label="D-Area"
                            isDisabled={!callSignValue}
                            options={formFieldOptions.dispatchAreaOptions}
                        />
                    </GridItem>
                    <StationAndBeatFields
                        control={control}
                        formFieldOptions={formFieldOptions}
                        beatSubdivisionLabel={statics.beatSubdivisionLabel ?? ''}
                    />
                    <GridItem colSpan={5}>
                        <FormSelect
                            name="equipment"
                            control={control}
                            label="Equipment"
                            isDisabled={!callSignValue}
                            options={formFieldOptions.equipmentOptions}
                            isMultiple={true}
                            helpTextTooltip="Edits made to equipment here will remain until this unit is logged off. Long term edits can be made on the units admin page."
                        />
                    </GridItem>
                    {!isFirstResponder && (
                        <>
                            <GridItem colSpan={5}>
                                <VehicleSelect
                                    formFieldOptions={formFieldOptions}
                                    isVehicleRequired={
                                        rollOutVehicleRequiredUnitSettingEnabled &&
                                        statics.isVehicleRequired
                                    }
                                />
                            </GridItem>
                            {staffCountEnabled && (
                                <GridItem colSpan={5}>
                                    <StaffCountInput
                                        setUserChangedStaffCount={setUserChangedStaffCount}
                                    />
                                </GridItem>
                            )}
                        </>
                    )}
                    {isFirstResponder && (
                        <GridItem colSpan={5}>
                            <HStack w="100%" alignItems="flex-start">
                                <VehicleSelect
                                    formFieldOptions={formFieldOptions}
                                    isVehicleRequired={
                                        rollOutVehicleRequiredUnitSettingEnabled &&
                                        statics.isVehicleRequired
                                    }
                                />
                                {staffCountEnabled && (
                                    <StaffCountInput
                                        setUserChangedStaffCount={setUserChangedStaffCount}
                                    />
                                )}
                            </HStack>
                        </GridItem>
                    )}
                    <GridItem colSpan={5}>
                        <Controller
                            name="personnel"
                            control={control}
                            rules={{
                                validate: (fieldValue) => {
                                    if (!statics.isMemberRequired) {
                                        return true;
                                    }
                                    const withoutEmptyValues = fieldValue.filter(
                                        isValidSelectedPersonnel
                                    );
                                    if (
                                        fieldValue.length === 0 ||
                                        withoutEmptyValues.length === 0
                                    ) {
                                        return 'Must include one or more personnel';
                                    }
                                    if (
                                        withoutEmptyValues.length !==
                                        uniqBy(
                                            withoutEmptyValues,
                                            (value: UserOption) => value.value
                                        ).length
                                    ) {
                                        return 'Cannot include duplicate personnel';
                                    }
                                    return true;
                                },
                            }}
                            render={({ formState }) => {
                                const personnelError =
                                    formState.isSubmitted || hasChangedPersonnel
                                        ? formState.errors.personnel?.root?.message
                                        : undefined;
                                return (
                                    <FormControl
                                        htmlFor="personnel"
                                        label="Personnel"
                                        isRequired={statics.isMemberRequired && !!callSignValue}
                                        errorMessage={personnelError}
                                        isInvalid={!!personnelError}
                                    >
                                        <VStack w="100%" alignItems="flex-start" spacing="4">
                                            <VStack w="100%" spacing="4">
                                                {rollOutUnitMemberOrderingEnabled ? (
                                                    <SortableList
                                                        items={fields}
                                                        getId={(item) => item.id}
                                                        firstItemLabel="Primary User"
                                                        dragHandleSize={
                                                            isFirstResponder ? 'medium' : 'small'
                                                        }
                                                        onReorder={(newOrder) => {
                                                            // fields is a stale snapshot; use getValues so unsaved edits survive reorder.
                                                            const currentValues =
                                                                form.getValues('personnel');
                                                            const oldIds = fields.map((f) => f.id);
                                                            replace(
                                                                newOrder.map(
                                                                    (item) =>
                                                                        currentValues[
                                                                            oldIds.indexOf(item.id)
                                                                        ]
                                                                )
                                                            );
                                                        }}
                                                        renderItem={(item) =>
                                                            renderPersonnelRow(
                                                                fields.findIndex(
                                                                    (f) => f.id === item.id
                                                                )
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    fields.map((_field, index) =>
                                                        renderPersonnelRow(index)
                                                    )
                                                )}
                                            </VStack>
                                            <AddPersonnelButton append={append} />
                                        </VStack>
                                    </FormControl>
                                );
                            }}
                        />
                    </GridItem>
                    <GridItem colSpan={5}>
                        <FormTextarea
                            name="shiftNotes"
                            control={control}
                            label="Notes"
                            isDisabled={!callSignValue}
                        />
                    </GridItem>
                    <GridItem colSpan={5}>
                        <Text variant="headingSm">Report Times</Text>
                    </GridItem>
                    <GridItem colSpan={5}>
                        <HStack w="100%">
                            <FormTimeInput
                                name="shiftStart"
                                control={control}
                                label="Shift Start"
                                isDisabled={!callSignValue}
                            />
                            <FormTimeInput
                                name="shiftEnd"
                                control={control}
                                label="Shift End"
                                isDisabled={!callSignValue}
                            />
                        </HStack>
                    </GridItem>
                </Grid>
            );
        }
);
